import { translate } from './i18n.js';
import { Notification } from '../services/Notification.js';

export class AccountModal {
    constructor(app, account) {
        this.app = app;
        this.account = account;
        this.pendingAccount = null;
        this.successCloseTimer = null;
        this.organisationSearchTimer = null;
        this.confirmedOrganisationForms = new WeakSet();
        this.organisationMembersPromise = null;
        this.recoveryEmail = '';
        this.fieldSequence = 0;
    }

    getContents() {
        return {
            signin: {
                titleKey: 'auth.signinTitle',
                icon: '/images/signin-grey.svg',
                path: 'signin',
                attachedLabel: true,
                render: (context) => this.renderSignin(context),
                cleanup: () => this.cleanup()
            },
            signup: {
                titleKey: 'auth.signupTitle',
                icon: '/images/signup-grey.svg',
                path: 'signup',
                attachedLabel: true,
                render: (context) => this.renderSignup(context),
                cleanup: () => this.cleanup()
            },
            account: {
                titleKey: 'account.title',
                icon: '/images/account-grey.svg',
                path: 'account',
                attachedLabel: true,
                render: (context) => this.renderAccount(context),
                cleanup: () => this.cleanup()
            }
        };
    }

    renderSignin(context, values = {}) {
        this.renderAuthView('signin', context, values);
    }

    renderSignup(context, values = {}) {
        this.pendingAccount = null;
        this.renderAuthView('signup', context, values);
    }

    renderAuthView(target, context, values = {}) {
        this.updateAuthViewMetadata(target, context);
        context.body.classList.add('account-modal-body', 'account-auth-body');
        context.body.replaceChildren(...this.createAuthViewContent(target, context, values));
    }

    createAuthViewContent(target, context, values = {}) {
        if (target === 'signup') {
            return [
                this.createIntro(translate('auth.signupIntro', context.locale)),
                this.createSignupForm(context, values),
                this.createSwitch('auth.hasAccount', 'auth.signinAction', 'signin', context)
            ];
        }

        const form = this.createSigninForm(context, values);

        return [
            this.createIntro(translate('auth.signinIntro', context.locale)),
            form,
            this.createPasswordRecoveryLink(context, form),
            this.createSwitch('auth.noAccount', 'auth.createAccount', 'signup', context)
        ];
    }

    updateAuthViewMetadata(target, context) {
        const signup = target === 'signup';

        context.setTitle(translate(signup ? 'auth.signupTitle' : 'auth.signinTitle', context.locale));
        context.setIcon(signup ? '/images/signup-grey.svg' : '/images/signin-grey.svg');
        context.setContentId(target);
    }

    renderAccount(context) {
        if (!this.app?.user) {
            context.close();
            context.open('signin');
            return;
        }

        const tabs = document.createElement('div');
        const panels = document.createElement('div');
        const track = document.createElement('div');
        const detailsPanel = document.createElement('section');
        const membersPanel = document.createElement('section');
        const detailsPanelId = this.getFieldId('details-panel');
        const membersPanelId = this.getFieldId('members-panel');
        const detailsTab = this.createAccountTab(
            'details',
            'account.detailsTab',
            context,
            true,
            detailsPanelId
        );
        const membersTab = this.createAccountTab(
            'members',
            'account.membersTab',
            context,
            false,
            membersPanelId
        );
        const hasOrganisation = Boolean(this.app.user.organisme);

        context.body.classList.add('account-modal-body', 'account-management-body');
        tabs.className = 'account-tabs';
        tabs.setAttribute('role', 'tablist');
        panels.className = 'account-tab-panels';
        track.className = 'account-tab-track';
        detailsPanel.id = detailsPanelId;
        detailsPanel.className = 'account-tab-panel';
        detailsPanel.setAttribute('role', 'tabpanel');
        detailsPanel.setAttribute('aria-labelledby', detailsTab.id);
        membersPanel.id = membersPanelId;
        membersPanel.className = 'account-tab-panel';
        membersPanel.setAttribute('role', 'tabpanel');
        membersPanel.setAttribute('aria-labelledby', membersTab.id);
        membersPanel.setAttribute('aria-hidden', 'true');
        membersPanel.inert = true;
        detailsTab.addEventListener('click', () => {
            this.activateAccountTab(tabs, detailsTab, track, [detailsPanel, membersPanel]);
        });
        membersTab.addEventListener('click', () => {
            this.activateAccountTab(tabs, membersTab, track, [detailsPanel, membersPanel]);
        });
        tabs.append(detailsTab, membersTab);
        track.append(detailsPanel, membersPanel);
        panels.append(track);
        context.body.replaceChildren(...(hasOrganisation ? [tabs, panels] : [detailsPanel]));
        this.renderAccountDetails(detailsPanel, context);
        if (hasOrganisation) {
            void this.renderOrganisationMembers(membersPanel, context);
        }
    }

    createAccountTab(id, labelKey, context, selected, panelId) {
        const button = document.createElement('button');

        button.type = 'button';
        button.id = this.getFieldId(`${id}-tab`);
        button.className = 'account-tab';
        button.dataset.accountTab = id;
        button.setAttribute('role', 'tab');
        button.setAttribute('aria-controls', panelId);
        button.setAttribute('aria-selected', String(selected));
        button.textContent = translate(labelKey, context.locale);
        if (selected) button.classList.add('active');
        return button;
    }

    activateAccountTab(tabs, activeTab, track, panels) {
        const activeId = activeTab.dataset.accountTab;

        tabs.querySelectorAll('.account-tab').forEach((tab) => {
            const active = tab === activeTab;

            tab.classList.toggle('active', active);
            tab.setAttribute('aria-selected', String(active));
        });
        track.classList.toggle('show-members', activeId === 'members');
        panels.forEach((panel) => {
            const active = panel.id === activeTab.getAttribute('aria-controls');

            panel.setAttribute('aria-hidden', String(!active));
            panel.inert = !active;
        });
    }

    renderAccountDetails(panel, context) {
        const user = this.app.user || {};
        const form = this.createForm('account-update');
        const firstName = this.createField(
            'prenom',
            'text',
            'auth.firstName',
            context.locale,
            'given-name'
        );
        const lastName = this.createField(
            'nom',
            'text',
            'auth.lastName',
            context.locale,
            'family-name'
        );
        const country = this.createCountryField('pays', 'auth.country', context.locale, true);
        const organisation = this.createOrganisationField(context, user);
        const email = this.createField('email', 'email', 'auth.email', context.locale, 'email');
        const telephone = this.createField('tel', 'tel', 'contact.telephone', context.locale, 'tel');
        const actions = document.createElement('div');
        const update = this.createSubmit('account.updateAction', context.locale);
        const changePassword = this.createChangePasswordButton(context);
        const remove = this.createDeleteAccountButton(context);

        telephone.querySelector('input').required = false;
        update.classList.remove('big');
        update.querySelector('img')?.remove();
        firstName.classList.add('ui-form-element-width-50');
        lastName.classList.add('ui-form-element-width-50');
        actions.className = 'ui-form-buttons account-form-actions account-management-actions';
        actions.append(update, changePassword, remove);
        form.dataset.submitLabel = 'account.updateAction';
        form.append(firstName, lastName, country, organisation, email, telephone, actions);
        form.elements.namedItem('prenom').value = user.prenom || '';
        form.elements.namedItem('nom').value = user.nom || '';
        form.elements.namedItem('email').value = user.email || '';
        form.elements.namedItem('tel').value = user.tel || user.telephone || user.phone || '';
        form.elements.namedItem('pays').value = this.normalizeCountryCode(user.pays) || form.elements.namedItem('pays').value;
        form.addEventListener('submit', (event) => this.submitAccountUpdate(event, context));
        panel.replaceChildren(form);
        this.loadOrganisations(form, user.organisme || '');
    }

    createChangePasswordButton(context) {
        const button = document.createElement('button');
        const label = document.createElement('span');

        button.type = 'button';
        button.className = 'ui-buttons medium';
        label.textContent = translate('account.changePasswordAction', context.locale);
        button.append(label);
        button.addEventListener('click', () => this.renderPasswordChange(context));
        return button;
    }

    renderPasswordChange(context) {
        const form = this.createForm('password-change');
        const password = this.createField(
            'password',
            'password',
            'account.newPassword',
            context.locale,
            'new-password'
        );
        const confirmation = this.createField(
            'password_confirmation',
            'password',
            'account.confirmPassword',
            context.locale,
            'new-password'
        );
        const actions = document.createElement('div');
        const submit = this.createSubmit('account.changePasswordAction', context.locale);

        [password, confirmation].forEach((field) => {
            const input = field.querySelector('input');

            input.minLength = 8;
            input.pattern = '(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9]).{8,}';
        });
        context.setTitle(translate('account.changePasswordTitle', context.locale));
        submit.querySelector('img')?.remove();
        actions.className = 'ui-form-buttons account-form-actions';
        actions.append(
            this.createRecoveryBackButton(context, () => {
                context.setTitle(translate('account.title', context.locale));
                this.renderAccount(context);
            }),
            submit
        );
        form.dataset.submitLabel = 'account.changePasswordAction';
        form.append(
            password,
            confirmation,
            this.createHint(translate('auth.passwordHint', context.locale)),
            actions
        );
        form.addEventListener('submit', (event) => this.submitPasswordChange(event, context));
        context.body.replaceChildren(
            this.createIntro(translate('account.changePasswordIntro', context.locale)),
            form
        );
        password.querySelector('input').focus();
    }

    async submitPasswordChange(event, context) {
        event.preventDefault();
        const form = event.currentTarget;
        const password = form.elements.namedItem('password');
        const confirmation = form.elements.namedItem('password_confirmation');

        confirmation.setCustomValidity(
            password.value === confirmation.value
                ? ''
                : translate('account.passwordMismatch', context.locale)
        );
        if (!form.reportValidity()) return false;

        this.setBusy(form, true, context.locale);
        try {
            await this.account.changePassword({
                password: password.value,
                _silentErrors: true
            });
            await this.account.signout({ notify: false, reload: false, boot: false });
            context.close();
            this.app.updateAuthMenu?.();
            new Notification({
                icon: 'check.svg',
                title: translate('account.changePasswordSuccessTitle', context.locale),
                content: translate('account.changePasswordSuccessMessage', context.locale),
                class: 'no-icon-filter',
                delay: 8000
            });
            this.app.simpleModal?.open('signin');
            return true;
        } catch (error) {
            this.setBusy(form, false, context.locale);
            this.showAccountError(error, context.locale);
            return false;
        }
    }

    createDeleteAccountButton(context, onClick = null) {
        const button = document.createElement('button');
        const icon = document.createElement('img');
        const label = document.createElement('span');

        button.type = 'button';
        button.className = 'ui-buttons delete medium';
        icon.src = '/images/trash-red.svg';
        icon.alt = '';
        icon.setAttribute('aria-hidden', 'true');
        label.textContent = translate('account.deleteAction', context.locale);
        button.append(icon, label);
        if (onClick !== false) {
            button.addEventListener('click', onClick || (() => this.renderDeleteConfirmation(context)));
        }
        return button;
    }

    async submitAccountUpdate(event, context) {
        event.preventDefault();
        const form = event.currentTarget;

        if (!form.reportValidity()) return;
        const data = Object.fromEntries(new FormData(form));

        if (this.requiresOrganisationConfirmation(form, data, this.app.user?.organisme || '')) {
            this.renderOrganisationConfirmation(context, form, data);
            return;
        }

        this.setBusy(form, true, context.locale);
        try {
            if (data.organisme === '__add__') {
                const organisationResponse = await this.account.createOrganisation(data.organisme_name, data.organisme_country);

                data.organisme = organisationResponse?.result?.organisme?.uid || '';
                if (!data.organisme) throw new Error(translate('auth.organisationCreateError', context.locale));
            }
            delete data.organisme_name;
            delete data.organisme_country;
            data._silentErrors = true;
            const response = await this.account.update(data);
            const pending = Boolean(response?.result?.requires_organisation_validation);

            if (pending) {
                await this.account.signout({ notify: false, reload: false, boot: false });
                context.close();
                this.app.updateAuthMenu?.();
                new Notification({
                    icon: 'warning.svg',
                    title: translate('account.pendingTitle', context.locale),
                    content: translate('account.pendingMessage', context.locale),
                    delay: 9000,
                    class: 'no-icon-filter'
                });
                return;
            }

            await this.account.storeAuthTokens(response);
            this.app.updateAuthMenu?.();
            new Notification({
                icon: 'check.svg',
                title: translate('account.updateSuccessTitle', context.locale),
                content: translate('account.updateSuccessMessage', context.locale),
                class: 'no-icon-filter'
            });
            this.organisationMembersPromise = null;
            this.renderAccount(context);
        } catch (error) {
            this.setBusy(form, false, context.locale);
            this.showAccountError(error, context.locale);
        }
    }

    async renderOrganisationMembers(panel, context) {
        const status = document.createElement('p');

        status.className = 'account-members-status';
        status.textContent = translate('account.membersLoading', context.locale);
        panel.replaceChildren(status);
        try {
            this.organisationMembersPromise ||= this.account.getOrganisationMembers()
                .then((response) => response?.result?.members || []);
            const members = await this.organisationMembersPromise;

            if (!members.length) {
                status.textContent = translate('account.membersEmpty', context.locale);
                return;
            }
            const list = document.createElement('div');

            list.className = 'account-members-list';
            members.forEach((member) => list.append(this.createMemberRow(member, context.locale)));
            panel.replaceChildren(list);
        } catch (error) {
            status.textContent = translate('account.membersError', context.locale);
        }
    }

    createMemberRow(member, locale) {
        const row = document.createElement('article');
        const identity = document.createElement('div');
        const name = document.createElement('strong');
        const status = document.createElement('span');

        row.className = 'account-member';
        identity.className = 'account-member-identity';
        name.textContent = [member.prenom, member.nom].filter(Boolean).join(' ');
        status.className = `account-member-status status-${member.status}`;
        status.textContent = translate(member.status === 2 ? 'account.memberValid' : 'account.memberPending', locale);
        identity.append(name);
        row.append(identity, status);
        return row;
    }

    renderDeleteConfirmation(context) {
        const panel = context.body.querySelector('.account-tab-panel');
        const tabs = context.body.querySelector('.account-tabs');
        const box = document.createElement('div');
        const title = document.createElement('strong');
        const message = document.createElement('p');
        const actions = document.createElement('div');
        const cancel = document.createElement('button');
        const confirm = this.createDeleteAccountButton(context, false);
        const support = document.createElement('a');
        const [messageBeforeSupport, messageAfterSupport = ''] = translate('account.deleteConfirmMessage', context.locale).split('{support}');

        box.className = 'account-delete-confirmation';
        title.textContent = translate('account.deleteConfirmTitle', context.locale);
        support.href = 'mailto:support@budo.events';
        support.textContent = translate('account.deleteConfirmSupport', context.locale);
        message.append(messageBeforeSupport, support, messageAfterSupport);
        actions.className = 'ui-form-buttons account-form-actions';
        cancel.type = 'button';
        cancel.className = 'ui-buttons medium cancel';
        cancel.textContent = translate('account.cancel', context.locale);
        cancel.addEventListener('click', () => {
            if (tabs) tabs.hidden = false;
            this.renderAccountDetails(panel, context);
        });
        confirm.querySelector('span').textContent = translate('account.deleteConfirmAction', context.locale);
        confirm.addEventListener('click', async () => {
            actions.querySelectorAll('button').forEach((button) => { button.disabled = true; });
            try {
                await this.account.delete();
                await this.account.signout({ notify: false, reload: false, boot: false });
                context.close();
                this.app.updateAuthMenu?.();
                new Notification({
                    icon: 'check.svg',
                    title: translate('account.deleteSuccessTitle', context.locale),
                    content: translate('account.deleteSuccessMessage', context.locale),
                    delay: 8000
                });
            } catch (error) {
                actions.querySelectorAll('button').forEach((button) => { button.disabled = false; });
                this.showAccountError(error, context.locale);
            }
        }, { once: true });
        actions.append(cancel, confirm);
        box.append(title, message, actions);
        if (tabs) tabs.hidden = true;
        panel.replaceChildren(box);
    }

    requiresOrganisationConfirmation(form, data, currentOrganisation = '') {
        if (this.confirmedOrganisationForms.has(form)) {
            this.confirmedOrganisationForms.delete(form);
            return false;
        }

        const selectedOrganisation = String(data.organisme || '').trim();
        return Boolean(selectedOrganisation && selectedOrganisation !== currentOrganisation);
    }

    renderOrganisationConfirmation(context, form, data) {
        const container = form.closest('.account-tab-panel') || context.body;
        const previousContent = [...container.childNodes];
        const box = document.createElement('div');
        const title = document.createElement('strong');
        const message = document.createElement('p');
        const actions = document.createElement('div');
        const cancel = document.createElement('button');
        const confirm = this.createSubmit('account.organisationConfirmAction', context.locale);
        const selectedOption = form.elements.namedItem('organisme')?.selectedOptions?.[0];
        const organisationName = data.organisme === '__add__'
            ? String(data.organisme_name || '').trim()
            : String(selectedOption?.textContent || '').trim();
        const restoreForm = () => container.replaceChildren(...previousContent);

        box.className = 'account-confirmation account-organisation-confirmation';
        title.textContent = translate('account.organisationConfirmTitle', context.locale, {
            organisation: organisationName
        });
        message.textContent = translate('account.organisationConfirmMessage', context.locale);
        actions.className = 'ui-form-buttons account-form-actions';
        cancel.type = 'button';
        cancel.className = 'ui-buttons medium cancel';
        cancel.textContent = translate('account.cancel', context.locale);
        cancel.addEventListener('click', restoreForm);
        confirm.type = 'button';
        confirm.classList.remove('big');
        confirm.classList.add('medium');
        confirm.querySelector('img')?.remove();
        confirm.addEventListener('click', () => {
            this.confirmedOrganisationForms.add(form);
            restoreForm();
            form.requestSubmit();
        }, { once: true });
        actions.append(cancel, confirm);
        box.append(title, message, actions);
        container.replaceChildren(box);
    }

    showAccountError(error, locale) {
        new Notification({
            icon: 'warning-red.svg',
            title: translate('account.errorTitle', locale),
            content: error?.apiError?.message || error?.message || translate('account.errorMessage', locale),
            delay: 8000
        });
    }

    createSigninForm(context, values = {}) {
        const form = this.createForm('signin');
        const email = this.createField('email', 'email', 'auth.email', context.locale, 'email');

        form.append(
            email,
            this.createField('password', 'password', 'auth.password', context.locale, 'current-password'),
            this.createFormButtons(this.createSubmit('auth.signinAction', context.locale))
        );
        form.elements.namedItem('email').value = values.email || '';
        form.addEventListener('submit', (event) => this.submitSignin(event, context));

        return form;
    }

    createPasswordRecoveryLink(context, form) {
        const forgotPassword = document.createElement('button');

        forgotPassword.type = 'button';
        forgotPassword.className = 'ui-buttons linktype account-recovery-link';
        forgotPassword.textContent = translate('auth.forgotPassword', context.locale);
        forgotPassword.addEventListener('click', () => {
            this.renderPasswordRecoveryRequest(context, form.elements.namedItem('email')?.value || '');
        });
        return forgotPassword;
    }

    renderPasswordRecoveryRequest(context, email = '') {
        const form = this.createForm('recovery-request');
        const emailField = this.createField('recover_email', 'email', 'auth.email', context.locale, 'email');
        const actions = document.createElement('div');
        const submit = this.createSubmit('auth.recoveryRequestAction', context.locale);

        this.recoveryEmail = String(email || this.recoveryEmail || '').trim();
        context.setTitle(translate('auth.recoveryTitle', context.locale));
        emailField.querySelector('input').value = this.recoveryEmail;
        submit.querySelector('img')?.remove();
        actions.className = 'ui-form-buttons account-form-actions';
        actions.append(
            this.createRecoveryBackButton(context, () => this.renderSignin(context, { email: this.recoveryEmail })),
            submit
        );
        form.dataset.submitLabel = 'auth.recoveryRequestAction';
        form.append(emailField, actions);
        form.addEventListener('submit', (event) => this.submitPasswordRecoveryRequest(event, context));
        context.body.replaceChildren(
            this.createIntro(translate('auth.recoveryRequestIntro', context.locale)),
            form
        );
        emailField.querySelector('input').focus();
    }

    async submitPasswordRecoveryRequest(event, context) {
        event.preventDefault();
        const form = event.currentTarget;

        if (!form.reportValidity()) return;
        this.recoveryEmail = String(form.elements.namedItem('recover_email')?.value || '').trim();
        this.setBusy(form, true, context.locale);
        try {
            await this.account.requestPasswordRecovery(this.recoveryEmail);
            this.renderPasswordRecoveryConfirm(context);
        } catch (error) {
            this.setBusy(form, false, context.locale);
            this.showRecoveryError(context.locale, 'auth.recoveryErrorMessage');
        }
    }

    renderPasswordRecoveryConfirm(context) {
        const form = this.createForm('recovery-confirm');
        const code = this.createRecoveryCodeField(context.locale);
        const password = this.createField('recover_p1', 'password', 'auth.newPassword', context.locale, 'new-password');
        const actions = document.createElement('div');
        const codeInput = code.querySelector('input');
        const passwordInput = password.querySelector('input');
        const submit = this.createSubmit('auth.recoveryConfirmAction', context.locale);

        passwordInput.minLength = 8;
        passwordInput.pattern = '(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9]).{8,}';
        submit.querySelector('img')?.remove();
        actions.className = 'ui-form-buttons account-form-actions';
        actions.append(
            this.createRecoveryBackButton(context, () => this.renderPasswordRecoveryRequest(context, this.recoveryEmail)),
            submit
        );
        form.dataset.submitLabel = 'auth.recoveryConfirmAction';
        form.append(
            code,
            password,
            this.createHint(translate('auth.passwordHint', context.locale)),
            actions
        );
        form.addEventListener('submit', (event) => this.submitPasswordRecoveryConfirm(event, context));
        context.body.replaceChildren(
            this.createIntro(translate('auth.recoveryConfirmIntro', context.locale)),
            form
        );
        codeInput.focus();
    }

    async submitPasswordRecoveryConfirm(event, context) {
        event.preventDefault();
        const form = event.currentTarget;
        const password = form.elements.namedItem('recover_p1');

        if (!form.reportValidity()) return;
        const recoverCode = [...form.querySelectorAll('.account-recovery-code input')]
            .map((input) => input.value)
            .join('');
        this.setBusy(form, true, context.locale);
        try {
            await this.account.confirmPasswordRecovery({
                recover_code: recoverCode,
                recover_p1: password.value,
                recover_email: this.recoveryEmail
            });
            new Notification({
                icon: 'check.svg',
                title: translate('auth.recoverySuccessTitle', context.locale),
                content: translate('auth.recoverySuccessMessage', context.locale),
                class: 'no-icon-filter'
            });
            this.renderSignin(context, { email: this.recoveryEmail });
        } catch (error) {
            this.setBusy(form, false, context.locale);
            const apiMessage = error?.apiError?.message || error?.message || '';
            const invalidCode = /code de récupération|recovery code/i.test(apiMessage);
            this.showRecoveryError(context.locale, invalidCode ? 'auth.recoveryInvalidCode' : 'auth.recoveryErrorMessage');
        }
    }

    createRecoveryCodeField(locale) {
        const field = document.createElement('div');
        const label = document.createElement('label');
        const inputs = document.createElement('div');

        field.className = 'ui-form-element ui-input-validation-code-container account-field account-validation-code-field';
        label.className = 'ui-input-label';
        label.textContent = translate('auth.recoveryCode', locale);
        inputs.className = 'ui-input-validation-code account-validation-code account-recovery-code';

        for (let index = 0; index < 6; index += 1) {
            const isNumber = index < 2;
            const input = document.createElement('input');

            input.type = isNumber ? 'number' : 'text';
            input.className = 'ui-input ui-input-validation-code-character';
            if (isNumber) input.classList.add('ui-input-number');
            input.name = `recover-code-${index + 1}`;
            input.required = true;
            input.dataset.codeKind = isNumber ? 'number' : 'letter';
            input.inputMode = isNumber ? 'numeric' : 'text';
            input.autocomplete = index === 0 ? 'one-time-code' : 'off';
            input.autocapitalize = 'none';
            if (isNumber) {
                input.min = '0';
                input.max = '9';
                input.step = '1';
            } else {
                input.maxLength = 1;
                input.pattern = '[A-Za-z]';
            }
            input.setAttribute('aria-label', `${translate('auth.recoveryCode', locale)} ${index + 1}`);
            input.addEventListener('input', (event) => this.handleRecoveryCodeInput(event, inputs));
            input.addEventListener('keydown', (event) => this.handleValidationCodeKeydown(event, inputs));
            input.addEventListener('paste', (event) => this.handleRecoveryCodePaste(event, inputs));
            this.bindValueState(input);
            inputs.append(input);
        }

        field.append(label, inputs);
        return field;
    }

    handleRecoveryCodeInput(event, container) {
        const input = event.currentTarget;
        const allowed = input.dataset.codeKind === 'number' ? /\D/g : /[^A-Za-z]/g;

        input.value = input.value.replace(allowed, '').slice(-1).toLowerCase();
        if (input.value) {
            const inputs = [...container.querySelectorAll('input')];
            inputs[inputs.indexOf(input) + 1]?.focus();
        }
    }

    handleRecoveryCodePaste(event, container) {
        const code = event.clipboardData?.getData('text').replace(/[^A-Za-z0-9]/g, '').slice(0, 6) || '';

        event.preventDefault();
        if (!/^[0-9]{2}[A-Za-z]{4}$/.test(code)) return;
        const inputs = [...container.querySelectorAll('input')];
        inputs.forEach((input, index) => { input.value = code[index].toLowerCase(); });
        inputs[inputs.length - 1].focus();
    }

    createRecoveryBackButton(context, onClick) {
        const button = document.createElement('button');

        button.type = 'button';
        button.className = 'ui-buttons medium cancel';
        button.textContent = translate('auth.back', context.locale);
        button.addEventListener('click', onClick);
        return button;
    }

    showRecoveryError(locale, messageKey) {
        new Notification({
            icon: 'warning.svg',
            title: translate('auth.recoveryErrorTitle', locale),
            content: translate(messageKey, locale),
            class: 'no-icon-filter',
            delay: 8000
        });
    }

    createSignupForm(context, values = {}) {
        const form = this.createForm('signup');
        const firstName = this.createField(
            'prenom',
            'text',
            'auth.firstName',
            context.locale,
            'given-name'
        );
        const lastName = this.createField(
            'nom',
            'text',
            'auth.lastName',
            context.locale,
            'family-name'
        );

        firstName.classList.add('ui-form-element-width-50');
        lastName.classList.add('ui-form-element-width-50');
        form.append(
            firstName,
            lastName,
            this.createCountryField('pays', 'auth.country', context.locale, true),
            this.createOrganisationField(context, values),
            this.createField('email', 'email', 'auth.email', context.locale, 'email'),
            this.createField('password', 'password', 'auth.password', context.locale, 'new-password'),
            this.createHint(translate('auth.passwordHint', context.locale)),
            this.createFormButtons(this.createSubmit('auth.signupAction', context.locale))
        );
        form.dataset.submitLabel = 'auth.signupAction';
        Object.entries(values).forEach(([name, value]) => {
            const input = form.elements.namedItem(name);

            if (input && typeof value === 'string') input.value = value;
        });
        form.elements.namedItem('organisme')?.dispatchEvent(new Event('change'));
        form.addEventListener('submit', (event) => this.submitSignup(event, context));
        this.loadOrganisations(form, values.organisme || '');

        return form;
    }

    createOrganisationField(context, values = {}) {
        const container = document.createElement('div');
        const field = document.createElement('label');
        const label = document.createElement('span');
        const select = document.createElement('select');
        const empty = document.createElement('option');
        const add = document.createElement('option');
        const addPanel = document.createElement('div');
        const similar = document.createElement('div');

        container.className = 'ui-form-element account-organisation';
        field.className = 'account-field';
        label.className = 'ui-input-label';
        select.id = this.getFieldId('organisme');
        label.htmlFor = select.id;
        label.textContent = translate('auth.organisation', context.locale);
        select.className = 'ui-select';
        select.name = 'organisme';
        select.autocomplete = 'organization';
        empty.value = '';
        empty.textContent = translate('auth.noOrganisation', context.locale);
        add.value = '__add__';
        add.textContent = translate('auth.addOrganisation', context.locale);
        select.append(empty, add);
        this.bindValueState(select);
        field.append(label, select);

        addPanel.className = 'ui-form account-organisation-add';
        addPanel.hidden = true;
        addPanel.append(
            this.createField('organisme_name', 'text', 'auth.organisationName', context.locale, 'organization'),
            this.createCountryField('organisme_country', 'auth.organisationCountry', context.locale, false)
        );
        similar.className = 'account-organisation-similar';
        similar.setAttribute('aria-live', 'polite');
        addPanel.append(similar);

        const nameInput = addPanel.querySelector('[name="organisme_name"]');
        const countryInput = addPanel.querySelector('[name="organisme_country"]');
        nameInput.required = false;
        countryInput.required = false;
        nameInput.value = values.organisme_name || '';
        countryInput.value = values.organisme_country || '';
        nameInput.addEventListener('input', () => this.scheduleOrganisationSearch(formContext(container), context));
        countryInput.addEventListener('change', () => this.scheduleOrganisationSearch(formContext(container), context));
        select.addEventListener('change', () => {
            const adding = select.value === '__add__';
            addPanel.hidden = !adding;
            nameInput.required = adding;
            countryInput.required = adding;
            if (adding) nameInput.focus();
        });

        container.append(field, addPanel);
        return container;

        function formContext(element) {
            return element.closest('form');
        }
    }

    async loadOrganisations(form, selectedUid = '') {
        const select = form.querySelector('[name="organisme"]');

        try {
            const response = await this.account.getOrganisations();
            const groups = response?.result?.groups || [];
            const addOption = select.querySelector('[value="__add__"]');

            select.querySelectorAll('optgroup').forEach((group) => group.remove());
            groups.forEach((group) => {
                const optgroup = document.createElement('optgroup');

                optgroup.label = this.getCountryLabel(group.country);
                group.organismes.forEach((organisme) => {
                    const option = document.createElement('option');

                    option.value = organisme.uid;
                    option.textContent = organisme.name;
                    optgroup.append(option);
                });
                select.insertBefore(optgroup, addOption);
            });
            if (selectedUid && select.querySelector(`option[value="${CSS.escape(selectedUid)}"]`)) {
                select.value = selectedUid;
            }
        } catch (error) {
            select.dataset.loadError = 'true';
        }
    }

    scheduleOrganisationSearch(form, context) {
        window.clearTimeout(this.organisationSearchTimer);
        this.organisationSearchTimer = window.setTimeout(() => this.searchSimilarOrganisations(form, context), 280);
    }

    async searchSimilarOrganisations(form, context) {
        if (!form) return;
        const name = form.elements.namedItem('organisme_name')?.value.trim() || '';
        const country = form.elements.namedItem('organisme_country')?.value.trim() || '';
        const container = form.querySelector('.account-organisation-similar');

        if (name.length < 3) {
            container.replaceChildren();
            return;
        }

        try {
            const response = await this.account.getOrganisations(name, country);
            const organismes = response?.result?.organismes || [];

            container.replaceChildren();
            if (!organismes.length) return;
            const title = document.createElement('span');
            title.textContent = translate('auth.similarOrganisations', context.locale);
            container.append(title);
            organismes.forEach((organisme) => {
                const button = document.createElement('button');

                button.type = 'button';
                button.className = 'ui-buttons small';
                button.textContent = `${organisme.name} — ${this.getCountryLabel(organisme.country)}`;
                button.addEventListener('click', async () => {
                    await this.loadOrganisations(form, organisme.uid);
                    form.elements.namedItem('organisme').value = organisme.uid;
                    form.querySelector('.account-organisation-add').hidden = true;
                    form.elements.namedItem('organisme_name').required = false;
                    form.elements.namedItem('organisme_country').required = false;
                    container.replaceChildren();
                });
                container.append(button);
            });
        } catch (error) {
            container.replaceChildren();
        }
    }

    createForm(kind) {
        const form = document.createElement('form');

        form.className = `ui-form account-form account-form-${kind}`;
        form.noValidate = true;
        return form;
    }

    createCountryField(name, labelKey, locale, required = true) {
        const wrapper = document.createElement('div');
        const label = document.createElement('label');
        const select = document.createElement('select');
        const inputId = this.getFieldId(name);

        wrapper.className = 'ui-form-element account-field';
        label.className = 'ui-input-label';
        label.htmlFor = inputId;
        label.textContent = translate(labelKey, locale);
        select.id = inputId;
        select.className = 'ui-select';
        select.name = name;
        select.required = required;
        select.autocomplete = 'country';
        this.getCountries().forEach((country) => {
            const option = document.createElement('option');

            option.value = country.code;
            option.textContent = country.label;
            select.append(option);
        });
        select.value = this.getDefaultCountry();
        this.bindValueState(select);
        wrapper.append(label, select);
        return wrapper;
    }

    getCountryLabel(countryCode) {
        const normalizedCode = this.normalizeCountryCode(countryCode);
        const country = this.getCountries().find((item) => item.code === normalizedCode);

        return country?.label || normalizedCode;
    }

    getCountries() {
        return this.normalizeCountries(this.app?.config?.countries?.list);
    }

    getDefaultCountry() {
        return this.normalizeCountryCode(this.app?.config?.countries?.default?.['iso-3166-1-alpha-2']);
    }

    normalizeCountries(countries) {
        if (!Array.isArray(countries)) return [];

        const knownCodes = new Set();

        return countries.reduce((normalized, country) => {
            const code = this.normalizeCountryCode(country?.['iso-3166-1-alpha-2']);
            const label = String(country?.label || '').trim();

            if (!code || !label || knownCodes.has(code)) return normalized;
            knownCodes.add(code);
            normalized.push({ code, label });
            return normalized;
        }, []);
    }

    normalizeCountryCode(countryCode) {
        const code = String(countryCode || '').trim().toUpperCase();

        return /^[A-Z]{2}$/.test(code) ? code : '';
    }

    createField(name, type, labelKey, locale, autocomplete) {
        const wrapper = document.createElement('div');
        const label = document.createElement('label');
        const input = document.createElement('input');
        const inputId = this.getFieldId(name);

        wrapper.className = 'ui-form-element account-field';
        label.className = 'ui-input-label';
        label.htmlFor = inputId;
        label.textContent = translate(labelKey, locale);
        input.id = inputId;
        input.className = 'ui-input';
        input.name = name;
        input.type = type;
        input.required = true;
        input.autocomplete = autocomplete;
        input.spellcheck = false;
        this.bindValueState(input);
        wrapper.append(label, input);
        return wrapper;
    }

    getFieldId(name) {
        this.fieldSequence += 1;
        return `account-${name}-${this.fieldSequence}`;
    }

    bindValueState(input) {
        const sync = () => {
            input.classList.toggle('has-value', String(input.value || '').trim() !== '');
        };

        input.addEventListener('input', sync);
        input.addEventListener('change', sync);
        queueMicrotask(sync);
    }

    createIntro(text) {
        const intro = document.createElement('p');
        intro.className = 'account-modal-intro';
        intro.textContent = text;
        return intro;
    }

    createHint(text) {
        const hint = document.createElement('p');
        hint.className = 'account-form-hint';
        hint.textContent = text;
        return hint;
    }

    createSubmit(labelKey, locale) {
        const button = document.createElement('button');
        const icon = document.createElement('img');
        const label = document.createElement('span');
        const icons = {
            'auth.signinAction': '/images/signin-grey.svg',
            'auth.createAccount': '/images/signup-grey.svg',
            'auth.signupAction': '/images/signup-grey.svg',
            'auth.validateAccount': '/images/check.svg',
            'account.updateAction': '/images/update-grey.svg'
        };

        button.type = 'submit';
        button.className = 'ui-buttons big';
        icon.src = icons[labelKey] || '/images/arrow-next-grey.svg';
        icon.alt = '';
        icon.setAttribute('aria-hidden', 'true');
        label.textContent = translate(labelKey, locale);
        button.append(icon, label);
        return button;
    }

    createFormButtons(...buttons) {
        const container = document.createElement('div');

        container.className = 'ui-form-buttons';
        container.append(...buttons);
        return container;
    }

    createSwitch(prefixKey, actionKey, target, context) {
        const paragraph = document.createElement('p');
        const button = document.createElement('button');
        const icon = document.createElement('img');
        const label = document.createElement('span');
        const icons = {
            signin: '/images/signin-grey.svg',
            signup: '/images/signup-grey.svg'
        };

        paragraph.className = 'account-switch';
        paragraph.append(document.createTextNode(`${translate(prefixKey, context.locale)} `));
        button.type = 'button';
        button.className = 'ui-buttons medium';
        icon.src = icons[target];
        icon.alt = '';
        icon.setAttribute('aria-hidden', 'true');
        label.textContent = translate(actionKey, context.locale);
        button.append(icon, label);
        button.addEventListener('click', () => {
            this.openAuthModal(target, context);
        });
        paragraph.append(button);
        return paragraph;
    }

    openAuthModal(target, context) {
        this.app?.home?.navigateToContent?.(target);
        context.close();
        context.open(target);
        return true;
    }

    async submitSignin(event, context) {
        event.preventDefault();
        const form = event.currentTarget;

        if (!form.reportValidity()) return;
        const data = Object.fromEntries(new FormData(form));

        this.setBusy(form, true, context.locale);

        try {
            data._silentErrors = true;
            const response = await this.account.connect(data);
            if (this.app.events?.loaded) {
                await this.app.events.list();
            }
            this.app.updateAuthMenu?.();
            this.renderSuccess(context, response, 'auth.signinSuccess');
        } catch (error) {
            this.showSigninError(error, context.locale);
            this.setBusy(form, false, context.locale);
        }
    }

    async submitSignup(event, context) {
        event.preventDefault();
        const form = event.currentTarget;

        if (!form.reportValidity()) return;
        const data = Object.fromEntries(new FormData(form));

        if (this.requiresOrganisationConfirmation(form, data)) {
            this.renderOrganisationConfirmation(context, form, data);
            return;
        }

        this.setBusy(form, true, context.locale);
        try {
            if (data.organisme === '__add__') {
                const organisationResponse = await this.account.createOrganisation(
                    data.organisme_name,
                    data.organisme_country
                );
                data.organisme = organisationResponse?.result?.organisme?.uid || '';
                if (!data.organisme) throw new Error(translate('auth.organisationCreateError', context.locale));
            }
            const response = await this.account.createAccount(data);
            this.pendingAccount = {
                ...data,
                uid: this.account.extractCreateAccountUid(response)
            };
            this.renderValidation(context);
        } catch (error) {
            this.setBusy(form, false, context.locale);
        }
    }

    renderValidation(context) {
        const form = this.createForm('validation');
        const codeField = this.createValidationCodeField(context.locale);
        const actions = document.createElement('div');

        actions.className = 'ui-form-buttons account-form-actions';
        actions.append(
            this.createBackButton(context),
            this.createSubmit('auth.validateAccount', context.locale)
        );
        form.append(
            codeField,
            actions
        );
        form.addEventListener('submit', (event) => this.submitValidation(event, context));
        context.body.replaceChildren(
            this.createIntro(translate('auth.validationIntro', context.locale)),
            form
        );
        codeField.querySelector('input').focus();
    }

    createValidationCodeField(locale) {
        const field = document.createElement('div');
        const label = document.createElement('label');
        const inputs = document.createElement('div');

        field.className = 'ui-form-element ui-input-validation-code-container account-field account-validation-code-field';
        label.className = 'ui-input-label';
        label.textContent = translate('auth.validationCode', locale);
        inputs.className = 'ui-input-validation-code account-validation-code';

        for (let index = 0; index < 6; index += 1) {
            const input = document.createElement('input');

            input.type = 'number';
            input.className = 'ui-input ui-input-number ui-input-validation-code-character';
            input.name = `code-${index + 1}`;
            input.required = true;
            input.min = '0';
            input.max = '9';
            input.step = '1';
            input.inputMode = 'numeric';
            input.autocomplete = index === 0 ? 'one-time-code' : 'off';
            input.setAttribute('aria-label', `${translate('auth.validationCode', locale)} ${index + 1}`);
            input.addEventListener('input', (event) => this.handleValidationCodeInput(event, inputs));
            input.addEventListener('keydown', (event) => this.handleValidationCodeKeydown(event, inputs));
            input.addEventListener('paste', (event) => this.handleValidationCodePaste(event, inputs));
            this.bindValueState(input);
            inputs.append(input);
        }

        field.append(label, inputs);
        return field;
    }

    handleValidationCodeInput(event, container) {
        const input = event.currentTarget;
        const digits = input.value.replace(/\D/g, '');

        input.value = digits.slice(-1);
        if (input.value) {
            const inputs = [...container.querySelectorAll('input')];
            inputs[inputs.indexOf(input) + 1]?.focus();
        }
    }

    handleValidationCodeKeydown(event, container) {
        const inputs = [...container.querySelectorAll('input')];
        const index = inputs.indexOf(event.currentTarget);

        if (event.key === 'Backspace' && !event.currentTarget.value && index > 0) {
            event.preventDefault();
            inputs[index - 1].value = '';
            inputs[index - 1].focus();
        } else if (event.key === 'ArrowLeft' && index > 0) {
            event.preventDefault();
            inputs[index - 1].focus();
        } else if (event.key === 'ArrowRight' && index < inputs.length - 1) {
            event.preventDefault();
            inputs[index + 1].focus();
        }
    }

    handleValidationCodePaste(event, container) {
        const digits = event.clipboardData?.getData('text').replace(/\D/g, '').slice(0, 6) || '';

        if (!digits) return;
        event.preventDefault();
        const inputs = [...container.querySelectorAll('input')];
        inputs.forEach((input, index) => { input.value = digits[index] || ''; });
        inputs[Math.min(digits.length, inputs.length) - 1].focus();
    }

    createBackButton(context) {
        const button = document.createElement('button');
        const label = document.createElement('span');

        button.type = 'button';
        button.className = 'ui-buttons medium cancel';
        label.textContent = translate('auth.back', context.locale);
        button.append(label);
        button.addEventListener('click', () => {
            const values = this.pendingAccount || {};

            this.pendingAccount = null;
            this.renderSignup(context, values);
        });
        return button;
    }

    async submitValidation(event, context) {
        event.preventDefault();
        const form = event.currentTarget;

        if (!form.reportValidity() || !this.pendingAccount?.uid) return;
        const code = [...form.querySelectorAll('.account-validation-code input')]
            .map((input) => input.value)
            .join('');

        this.setBusy(form, true, context.locale);
        try {
            const response = await this.account.validateCreateAccountEmail(this.pendingAccount.uid, code);
            this.renderSuccess(context, response, 'auth.signupSuccess');
        } catch (error) {
            this.setBusy(form, false, context.locale);
        }
    }

    renderSuccess(context, response, messageKey) {
        if (messageKey === 'auth.signupSuccess') {
            this.renderSignupSuccess(context, response);
            return;
        }

        this.renderSigninSuccess(context, response);
    }

    renderSigninSuccess(context, response) {
        const user = response?.result?.user || this.app?.user || {};
        const realname = user.realname || [user.prenom, user.nom].filter(Boolean).join(' ');
        const message = translate('auth.signinSuccess', context.locale, { realname });

        this.renderAccountConfirmation(
            context,
            message,
            1300,
            () => {
                void this.app.handleOrganisationValidationLink?.();
                void this.app.handleEventValidationLink?.();
            }
        );
    }

    renderSignupSuccess(context, response) {
        const requiresOrganisationValidation = Boolean(response?.result?.requires_organisation_validation);

        this.renderAccountConfirmation(
            context,
            translate(requiresOrganisationValidation ? 'auth.signupPendingOrganisation' : 'auth.signupSuccess', context.locale),
            requiresOrganisationValidation ? 3200 : 1500
        );
    }

    renderAccountConfirmation(context, text, duration, afterClose = null) {
        const box = document.createElement('div');
        const icon = document.createElement('img');
        const message = document.createElement('strong');

        box.className = 'account-success account-confirmation-success';
        icon.className = 'account-success-icon';
        icon.src = '/images/check.svg';
        icon.alt = '';
        icon.setAttribute('aria-hidden', 'true');
        message.textContent = text;
        box.append(icon, message);
        context.modal.classList.add('account-modal-success-state');
        context.body.replaceChildren(box);
        this.app.updateAuthMenu?.();
        this.clearSuccessCloseTimer();
        this.successCloseTimer = window.setTimeout(() => {
            context.close();
            if (typeof afterClose === 'function') {
                window.setTimeout(afterClose, 350);
            }
        }, duration);
    }

    clearSuccessCloseTimer() {
        if (this.successCloseTimer) {
            window.clearTimeout(this.successCloseTimer);
            this.successCloseTimer = null;
        }
    }

    cleanup() {
        this.clearSuccessCloseTimer();
        window.clearTimeout(this.organisationSearchTimer);
        this.organisationSearchTimer = null;
        this.organisationMembersPromise = null;
        this.recoveryEmail = '';
    }

    setBusy(form, busy, locale) {
        form.classList.toggle('loading', busy);
        form.querySelectorAll('input, select, button').forEach((element) => { element.disabled = busy; });
        const submit = form.querySelector('[type="submit"]');
        const submitLabel = submit?.querySelector('span');
        const idleLabel = form.dataset.submitLabel
            || (form.classList.contains('account-form-signin') ? 'auth.signinAction' : form.classList.contains('account-form-validation') ? 'auth.validateAccount' : 'auth.signupAction');
        if (submitLabel) submitLabel.textContent = translate(busy ? 'auth.loading' : idleLabel, locale);
    }

    showSigninError(error, locale) {
        const apiMessage = error?.apiError?.message || this.account.api?.lastError?.message || error?.message || '';
        const invalidCredentials = /identification invalide|invalid credentials/i.test(apiMessage);

        new Notification({
            icon: 'warning.svg',
            title: translate('auth.signinErrorTitle', locale),
            content: translate(invalidCredentials ? 'auth.invalidCredentials' : 'auth.signinErrorMessage', locale),
            class: 'no-icon-filter'
        });
    }
}
